import { useState, useEffect } from "react";
import {
  Box,
  Column,
  CloseIcon,
  IconButton,
  Tooltip,
  Text,
  useClipboard,
  Button,
  CopyIcon,
  CheckIcon,
  HtMedia,
  isVideoSrc,
} from "@hightouch/htui";
import { Card } from "src/components/card";
import { truncateMiddle } from "src/components/llm/utils/truncate-middle";
import { FullScreenImageOverlay } from "./full-screen-image-overlay";

export const IMAGE_THUMBNAIL_SIZES = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 64,
};

export const ImageThumbnail = ({
  fileName,
  imageSrc,
  onRemove,
  size = "xs",
  showImageNameInline,
  onExpand,
  onError,
}: {
  fileName: string;
  imageSrc: string;
  onRemove?: () => void;
  size?: "xs" | "sm" | "md" | "lg";
  showImageNameInline?: boolean;
  /**
   * When provided, clicking the thumbnail delegates to a shared gallery overlay
   * (so siblings can be paged through with arrow keys) instead of opening this
   * thumbnail's own single-image overlay.
   */
  onExpand?: () => void;
  /** The source failed to load, so a caller can show something other than a broken tile. */
  onError?: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const imgSize = IMAGE_THUMBNAIL_SIZES[size];
  const isVideo = isVideoSrc(imageSrc);
  const clipboard = useClipboard(fileName);

  useEffect(() => {
    if (showCopied) {
      const timer = setTimeout(() => {
        setShowCopied(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showCopied]);

  return (
    <>
      <Column gap={1} alignItems="flex-start">
        <Tooltip message={fileName} placement="bottom">
          <Card
            p={0}
            overflow="hidden"
            position="relative"
            width={imgSize}
            height={imgSize}
            onClick={() => (onExpand ? onExpand() : setIsExpanded(true))}
          >
            <HtMedia
              src={imageSrc}
              alt={fileName}
              width="100%"
              height="100%"
              objectFit="cover"
              video={{ playback: "none" }}
              onError={onError}
            />
            {onRemove && (
              <Box position="absolute" top={0} right={0.5} zIndex={1}>
                <IconButton
                  icon={CloseIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  size="xs"
                  shape="circle"
                  aria-label="remove"
                  bg="white"
                />
              </Box>
            )}
          </Card>
        </Tooltip>
        {showImageNameInline && (
          <Box width={imgSize} overflow="hidden">
            <Box
              as={Button}
              size="sm"
              variant="tertiary"
              onClick={(e) => {
                e.stopPropagation();
                clipboard.onCopy();
                setShowCopied(true);
              }}
              icon={showCopied ? CheckIcon : CopyIcon}
              width="100%"
              justifyContent="flex-start"
              overflow="hidden"
            >
              <Box
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                minWidth={0}
                flex={1}
              >
                <Text size="sm" color="text.secondary">
                  {truncateMiddle(fileName)}
                </Text>
              </Box>
            </Box>
          </Box>
        )}
      </Column>

      {!onExpand && (
        <FullScreenImageOverlay
          imageSrc={imageSrc}
          fileName={fileName}
          isOpen={isExpanded}
          onClose={() => setIsExpanded(false)}
          maxHeight={isVideo ? "500px" : undefined}
        />
      )}
    </>
  );
};
